import { DeleteFilled, EditFilled } from '@ant-design/icons';
import { Button, Spin, Table, TablePaginationConfig } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import { ColumnsProps, ScrollType, buildColumn, getDefaultTablePaginationConfig, useHandleOnChangeTableLogic } from '../../../Helper';
import { PaginationSetting } from '../../../model/CommonModel';
import { Instance, InstanceColumn } from '../../../model/VelocityVisionModel';
import { ModalConfirmationList, SearchColumn } from '../../common';
import styles from './listView.module.scss';

type ListViewProps = {
	data: Instance[];
	isUserAdmin: boolean;
	loadData: (pagination: PaginationSetting) => Promise<void>;
	paginationSetting: PaginationSetting;
	deleteInstances: (id: number) => Promise<boolean>;
	onAddNewButtonClick: (visionItemId: number) => void;
	onTryOpenOrFocusTab: (url: string, name: string) => void;
};

const maxLength: number = 20;

const ListView: React.FC<ListViewProps> = ({ data, isUserAdmin, loadData, paginationSetting, deleteInstances, onAddNewButtonClick, onTryOpenOrFocusTab }) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [instances, setInstances] = useState<InstanceColumn[]>([] as InstanceColumn[]);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [scroll, setScroll] = useState<ScrollType>({ y: window.innerHeight - 290 });

	const isSearchPerformed = useRef<boolean>(false);
	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	useEffect(() => {
		const handleResize = () => {
			setScroll({ y: window.innerHeight - 290 });
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	useEffect(() => {
		if (data) {
			const formattedData = mapToPresentationFormat(data);
			setInstances(formattedData);
		}
	}, [JSON.stringify(data)]);

	const handleChangePagination = (page: number, pageSize: number) => {
		if (pageSize !== paginationSetting.PageSize) {
			clearSelection();
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		handleChangePagination,
		paginationSetting.PageNumber,
		paginationSetting.PageSize,
		paginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	const clearSelection = () => {
		setSelectedRowKeys([]);
	};

	const handleOnSearch = (searchedValue: string) => {
		setIsLoading(true);
		loadData({ ...paginationSetting, SearchedValue: searchedValue }).finally(() => {
			setIsLoading(false);
		});
	};

	const mapToPresentationFormat = (data: Instance[]): InstanceColumn[] => {
		if (data) {
			return data.map<InstanceColumn>(d => ({
				key: `${d.Id}`,
				Id: d.Id,
				Name: d.Name,
				InstanceUrl: (
					<a
						href={d.InstanceUrl}
						onClick={e => {
							e.preventDefault();
							onTryOpenOrFocusTab(d.InstanceUrl, d.Name);
						}}
						target='_blank'
						rel='noopener noreferrer'>
						{d.InstanceUrl}
					</a>
				),
				Action: createActionElements(d),
			}));
		}

		return [] as InstanceColumn[];
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const columns: ColumnsProps<InstanceColumn>[] = [
		{
			...buildColumn(_('Name'), 'Name', '250px', 'start'),
			editable: false,
			sorter: true,
			...SearchColumn({
				maxLength,
				dataIndex: 'InstanceUrl',
				reset: undefined,
				label: 'Name',
				notVisible: undefined,
				resetSearch: shouldResetSearchColumn,
				clearSelection,
				handleResetSearch: () => setShouldResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => handleOnSearch(searchedValue),
				setSearchedValue: undefined,
				searchColumnId: undefined,
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
				disableInternalSearchMode: true,
			}),
		},
		{
			...buildColumn(_('InstanceUrl'), 'InstanceUrl', 'auto', 'start'),
			editable: false,
		},
		{
			...buildColumn(_('Actions'), 'Action', '100px'),
			editable: false,
		},
	];

	const handleOnEditRow = (key: string) => {
		const idItemToEdit: number = data.find(d => d.Id.toString() == key).Id;
		onAddNewButtonClick(idItemToEdit);
	};

	const handleOnDeleteRow = (key: string) => {
		const itemToDelete: Instance = data.find(d => d.Id.toString() == key);
		if (itemToDelete) {
			ModalConfirmationList({
				type: _('Delete'),
				translationKey: 'AreYouSureYouWantToDeleteVelocityVisionInstance',
				data: [itemToDelete],
				onConfirm: () => {
					deleteInstances(itemToDelete.Id)
						.then(success => {
							if (success) {
								setIsLoading(true);
							}
						})
						.finally(() => {
							setIsLoading(false);
						});
				},
			});
		}
	};

	const createActionElements = (url: Instance): React.ReactNode => {
		const keyId: string = url.Id.toString();
		if (isUserAdmin) {
			const content = (
				<div className={styles.actionContainer}>
					<Button
						className={styles.buttonStyle}
						id={`velocityVisionListViewActionEditButton-${keyId}`}
						key='edit'
						type='default'
						title={_('Edit')}
						onClick={() => handleOnEditRow(keyId)}
					>
						<EditFilled />
					</Button>
					<Button
						className={styles.buttonStyle}
						id={`velocityVisionListViewActionDeleteButton-${keyId}`}
						key='delete'
						type='default'
						title={_('Delete')}
						onClick={() => handleOnDeleteRow(keyId)}
					>
						<DeleteFilled />
					</Button>
				</div>
			);

			return content;
		} else {
			return <></>;
		}
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<InstanceColumn>) => {
		if (isSearchPerformed.current) return;
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection: clearSelection,
			filters,
			handleChangePagination: handleChangePagination,
			isSearchPerformed: isSearchPerformed,
			pagination,
			shouldResetSearchColumn: false,
			sorter,
			tablePaginationSetting: paginationSetting,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			setIsLoading(true);
			loadData({
				...paginationSetting,
				PageNumber: current,
				PageSize: pageSize,
				SortDirection: sortOrder,
				SortField: sortField?.toString(),
			}).finally(() => {
				setIsLoading(false);
			});
		}
	};

	return (
		<Spin tip={`${_('Loading')}...`} size='large' spinning={isLoading}>
			<div className={styles.tableContainer}>
				<Table
					id='velocityVisionListViewTable'
					size='small'
					dataSource={instances}
					columns={columns}
					pagination={pagination}
					scroll={scroll}
					onChange={handleOnChangeTable}
				/>
			</div>
		</Spin>
	);
};

export { ListView };
